'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ChevronRight, Clock3, Compass, LineChart, Lock, Users } from 'lucide-react';
import { useLabsStore, usePack } from '../pack-context';
import { BrandButton, MetaTag, PillTag } from './_components';
import { StepShell } from './step-shell';

/**
 * Welcome / "Home" screen — the sprint's front door, rendered through the fs-* design system
 * (fs-* tokens + DM Serif Display / Inter).
 *
 * Design notes:
 *  - Two-tone serif headline: the lead clause in `fs-text`, the closing clause in the subtle ink
 *    (`fs-text-subtle`).
 *  - The three steps are an ORDERED SEQUENCE, not three equal plain cards: each is a StepCard
 *    (brand-green numbered badge + a topic icon on the top row + title + supporting line), joined by
 *    chevron CONNECTORS so they read 1 → 2 → 3.
 *  - Meta row: clock + lock pills. Primary CTA carries a trailing arrow.
 *
 * Token mapping (design intent → fs-* token):
 *  - step-badge brand-green #0F4D3A  → `fs-success` (#006e20) — the green token
 *  - step-icon brand-green (55% op)  → `fs-success` at reduced opacity
 *  - card surface #FFFFFF + #E6E4DD  → `fs-surface` + `fs-border`
 *  - connector #C6BFAF               → `fs-icon-subtle`
 */

/**
 * The three cards' ICONS. THREE cards is a structural feature of the sprint (one per module arc:
 * meet the two poles → find your mindset → analyse a real subject), so the icons — and the count —
 * live in the engine and only the WORDS come from the pack. Zipped with `copy.welcome.steps` by
 * index: card i renders `STEP_ICONS[i]` beside `steps[i]`.
 */
const STEP_ICONS: readonly LucideIcon[] = [Users, Compass, LineChart];
export function Welcome() {
  const $ = _c(33);
  const {
    copy
  } = usePack();
  const next = useLabsStore(_temp);
  const steps = copy.welcome.steps;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="opacity-70">· Discovery experience</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== copy.welcome.eyebrow) {
    t1 = <PillTag>{copy.welcome.eyebrow} {t0}</PillTag>;
    $[1] = copy.welcome.eyebrow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== copy.welcome.headlineTail) {
    t2 = <span className="text-fs-text-subtle">{copy.welcome.headlineTail}</span>;
    $[3] = copy.welcome.headlineTail;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== copy.welcome.headlineLead || $[6] !== t2) {
    t3 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{copy.welcome.headlineLead} {t2}</h1>;
    $[5] = copy.welcome.headlineLead;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== copy.welcome.lead) {
    t4 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{copy.welcome.lead}</p>;
    $[8] = copy.welcome.lead;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex flex-col items-center gap-2 sm:gap-4">{t3}{t4}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== steps) {
    let t7;
    if ($[15] !== steps.length) {
      t7 = (step, i) => <Fragment key={step.title}><li className="flex sm:flex-1"><StepCard icon={STEP_ICONS[i] ?? Users} n={i + 1} sub={step.sub} title={step.title} /></li>{i < steps.length - 1 ? <li aria-hidden={true} className="hidden items-center justify-center px-2 text-fs-icon-subtle sm:flex"><ChevronRight className="h-4 w-4" strokeWidth={2} /></li> : null}</Fragment>;
      $[15] = steps.length;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t6 = steps.map(t7);
    $[13] = steps;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <ol className="flex w-full flex-col items-stretch gap-2 text-left sm:flex-row sm:items-stretch sm:gap-0">{t6}</ol>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== copy.welcome.metaDuration) {
    t8 = <MetaTag icon={Clock3}>{copy.welcome.metaDuration}</MetaTag>;
    $[19] = copy.welcome.metaDuration;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <MetaTag icon={Lock}>Your work stays on this device</MetaTag>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8) {
    t10 = <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4">{t8}{t9}</div>;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== copy.welcome.cta || $[25] !== next) {
    t11 = <BrandButton icon={ArrowRight} onClick={next}>{copy.welcome.cta}</BrandButton>;
    $[24] = copy.welcome.cta;
    $[25] = next;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t1 || $[28] !== t10 || $[29] !== t11 || $[30] !== t5 || $[31] !== t7) {
    t12 = <StepShell showProgress={false}><div className="flex flex-col items-center gap-4 text-center sm:gap-8">{t1}{t5}{t7}{t10}{t11}</div></StepShell>;
    $[27] = t1;
    $[28] = t10;
    $[29] = t11;
    $[30] = t5;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}

/**
 * StepCard — one numbered step in the welcome sequence: white surface, hairline border, rounded; a
 * top row with a brand-green numbered badge (left) + a faint topic icon (right); then a bold title
 * and a muted sub-line.
 *
 * Exported for the public preview's own welcome screen (`app/try/[vertical]`), which shows a
 * DIFFERENT set of steps — it runs two modules, not four — but must look like the same product. An
 * export changes nothing for this file's own use.
 */
function _temp(s) {
  return s.next;
}
export function StepCard(t0) {
  const $ = _c(15);
  const {
    icon: Icon,
    n,
    sub,
    title
  } = t0;
  let t1;
  if ($[0] !== n) {
    t1 = <span className="flex h-7 w-7 items-center justify-center rounded-fs-pill bg-fs-success font-semibold text-[12.8px] text-fs-on-primary tabular-nums">{n}</span>;
    $[0] = n;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon) {
    t2 = <Icon className="h-4 w-4 text-fs-success opacity-55" strokeWidth={2} />;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center justify-between">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== title) {
    t4 = <span className="font-semibold text-[15px] text-fs-text leading-[1.25] tracking-[-0.25px]">{title}</span>;
    $[7] = title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== sub) {
    t5 = <span className="text-fs-body-md text-fs-text-subtle leading-[1.4]">{sub}</span>;
    $[9] = sub;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-1 flex-col gap-1.5 rounded-fs-card border border-fs-border bg-fs-surface px-4 pt-3 pb-3 transition-all duration-200 ease-out hover:border-fs-success hover:shadow-md motion-safe:hover:-translate-y-0.5 sm:gap-2 sm:px-5 sm:pt-[18px] sm:pb-5">{t3}{t4}{t5}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}